/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';

import { Button, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Icon from 'bundles/iconfont/Icon';
import CplusDeflection from 'bundles/my-purchases-graphql/components/modals/cancelDeflection/CplusDeflection';
import { isCplusPromoCode } from 'bundles/my-purchases-graphql/components/modals/cancelDeflection/constants';
import Naptime from 'bundles/naptimejs';
import PromotionDetailsV1 from 'bundles/naptimejs/resources/promotionDetails.v1';
import SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import UserTestimonial from 'bundles/payments/components/socialProofCheckoutExperiment/UserTestimonial';
import steps from 'bundles/refund/constants/steps';
import type { StepType } from 'bundles/refund/constants/steps';

import _t from 'i18n!nls/my-purchases-graphql';

type PropsFromCaller = {
  updateStep: (step: StepType) => void;
  cancelSubscription: () => Promise<void>;
  promoCode: string;
  subscriptionId: string;
  promotionDetails?: PromotionDetailsV1;
  onError: () => void;
};

type NaptimeProps = {
  promoCode: string;
};

type PropsFromContext = {
  executeMutation: <Action, Response>(action: Action) => Promise<Response>;
  addToast: (message: string) => void;
};

export type CancelDeflectionBodyProps = Props & {
  didCancelOrApplyDiscount: boolean;
  handleCancelSubscription: () => void;
  handleApplyDiscount: () => void;
};

type Props = PropsFromCaller & NaptimeProps & PropsFromContext;

const useStyles = (theme: Theme) => ({
  rcCancelDeflectionModal: css({
    overflowY: 'hidden',
  }),
  modalBody: css({
    display: 'flex',
    [breakpoints.down('xs')]: {
      marginTop: '72px',
      display: 'block',
    },
  }),
  rightContainer: css({
    paddingTop: '20px',
    [breakpoints.down('xs')]: {
      display: 'none',
    },
  }),
  headline: css({
    fontFamily: 'OpenSans',
    fontWeight: 300,
    fontSize: '18px',
    height: '28px',

    [breakpoints.down('xs')]: {
      fontSize: '24px',
    },
  }),
  discountHeadline: css({
    fontWeight: 'bold',
    fontFamily: 'OpenSans',
    fontSize: '64px !important',
    color: '#2A73CC !important',
    margin: 0,
    lineHeight: '60px',

    [breakpoints.down('xs')]: {
      fontSize: '48px',
      marginTop: '10px',
      marginBottom: '18px',
    },
  }),
  infoText: css({
    maxWidth: '280px',
    fontWeight: 300,
    fontFamily: 'OpenSans',
    fontSize: '14px',
    marginBottom: '60px',
    lineHeight: '24px',

    [breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  }),
  highlight: css({
    fontWeight: 'bold',
  }),
  applyDiscountInfo: css({
    marginBottom: '78px',
  }),
  mobileTestimonial: css({
    display: 'none',

    [breakpoints.down('xs')]: {
      display: 'block',
    },
  }),
  modalFooter: css({
    display: 'flex',

    [breakpoints.down('xs')]: {
      width: '100%',
      bottom: 0,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '200px',
    },
  }),
  cancelSubscription: css({
    [breakpoints.down('xs')]: {
      marginRight: 0,
      width: '180px',
      marginTop: '24px',
    },
  }),
  confirmButtonContainer: css({
    position: 'relative',
  }),
  baseActionBtn: css({
    fontWeight: 'bold',
    fontSize: '14px',
    height: '48px',
  }),
  confirmButton: css({
    marginRight: '18px',
    [breakpoints.down('xs')]: {
      marginRight: 0,
      width: '180px',
    },
  }),
  cifSpin: css({
    position: 'absolute',
    top: '16px',
    left: '16px',
  }),
});

export const CancelDeflectionModalBase = (props: Props) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [didCancelOrApplyDiscount, setDidCancelOrApplyDiscount] = useState(false);
  const { cancelSubscription, updateStep, subscriptionId, promoCode, onError, promotionDetails, executeMutation } =
    props;

  const handleCancelSubscription = async () => {
    setDidCancelOrApplyDiscount(true);
    try {
      await cancelSubscription();
      updateStep(steps.confirmation);
    } catch {
      // This is handled by cancelSubscription.
    }
  };

  const handleApplyDiscount = async () => {
    setDidCancelOrApplyDiscount(true);
    const applyDiscountAction = SubscriptionsV1.action(
      'applyDiscountToSubscription',
      {},
      { promoCodeId: promoCode, id: subscriptionId }
    );

    try {
      await executeMutation(applyDiscountAction);
      // Adding an artificial delay here as it is not guaranteed that
      // subscriptions.v1 action=applyDiscountToSubscription will update data  by the time we redirect
      setTimeout(() => {
        redirect.setLocation('/my-purchases');
      }, 500);
    } catch {
      onError();
    }
  };

  const bodyProps: CancelDeflectionBodyProps = {
    ...props,
    didCancelOrApplyDiscount,
    handleCancelSubscription,
    handleApplyDiscount,
  };

  if (isCplusPromoCode(promoCode)) {
    return <CplusDeflection {...bodyProps} />;
  }
  return (
    <div css={styles.rcCancelDeflectionModal}>
      <div css={styles.modalBody}>
        <div>
          <div css={styles.headline}>{_t('Keep learning for')}</div>
          <div css={styles.discountHeadline}>
            <FormattedMessage message={_t('{percent}% OFF')} percent={promotionDetails?.discountPercent} />
          </div>
          <div css={styles.infoText}>
            <FormattedMessage
              message={_t('Continue your course and we’ll give you an {automatic} {discount} on your next bill.')}
              automatic={<span css={styles.highlight}>{_t('automatic')}</span>}
              discount={
                <span css={styles.highlight}>
                  <FormattedMessage message={_t('{percent}% discount')} percent={promotionDetails?.discountPercent} />
                </span>
              }
            />
          </div>
          <div css={styles.mobileTestimonial}>
            <UserTestimonial renderCondenseTestimonial={true} />
          </div>
          <div css={styles.modalFooter}>
            <div css={styles.confirmButtonContainer}>
              {didCancelOrApplyDiscount && <Icon name="spinner" css={styles.cifSpin} />}
              <Button
                variant="primary"
                component={TrackedButton}
                type="button"
                onClick={handleApplyDiscount}
                trackingName="apply_discount"
                withVisibilityTracking={true}
                requireFullyVisible={false}
                className="primary"
                data-testid="apply_discount"
                css={[styles.baseActionBtn, styles.confirmButton]}
                disabled={didCancelOrApplyDiscount}
              >
                {_t('Apply the Discount')}
              </Button>
            </div>
            <Button
              variant="secondary"
              component={TrackedButton}
              type="button"
              onClick={handleCancelSubscription}
              trackingName="cancel_subscription"
              data-testid="cancel_subscription"
              withVisibilityTracking={true}
              requireFullyVisible={false}
              className="secondary"
              css={[styles.baseActionBtn, styles.cancelSubscription]}
              disabled={didCancelOrApplyDiscount}
            >
              {_t('Cancel Subscription')}
            </Button>
          </div>
        </div>
        <div css={styles.rightContainer}>
          <UserTestimonial renderCondenseTestimonial={true} />
        </div>
      </div>
    </div>
  );
};

export const CancelDeflectionModal = compose<Props, PropsFromCaller>(
  Naptime.createContainer<PropsFromCaller>(({ promoCode }) => {
    return {
      promotionDetails: PromotionDetailsV1.byPromoCodeId(promoCode, {
        fields: ['discountPercent'],
      }),
    };
  }),
  getContext({
    executeMutation: PropTypes.func,
  })
)(CancelDeflectionModalBase);

export default Naptime.createContainer(({ promoCode }: NaptimeProps) => {
  return {
    promotionDetails: PromotionDetailsV1.byPromoCodeId(promoCode, {
      fields: ['discountPercent'],
    }),
  };
  // @ts-expect-error TSMIGRATION
})(CancelDeflectionModal);
